import { useRef, useState, useLayoutEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import styled from "@emotion/styled";
import {
  surface,
  text,
  border as borderToken,
  controlFontSize,
  spacing,
  borderRadius,
  fontWeight,
  lineHeight,
  shadow,
  zIndex as zIndexTokens,
} from "@atlas-int/tokens";

const IconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  cursor: help;
`;

const InfoIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <circle
      cx="8"
      cy="8"
      r="7"
      stroke="currentColor"
      strokeWidth="1.5"
      opacity="0.5"
    />
    <text
      x="8"
      y="12"
      textAnchor="middle"
      fill="currentColor"
      fontSize="10"
      fontWeight="600"
      fontFamily="sans-serif"
      opacity="0.5"
    >
      i
    </text>
  </svg>
);

const TooltipBubble = styled.div`
  position: fixed;
  background: ${surface.raised};
  color: ${text.primary};
  font-size: ${controlFontSize.sm};
  font-weight: ${fontWeight.normal};
  line-height: ${lineHeight.snug};
  padding: ${spacing[2]} ${spacing[3]};
  border-radius: ${borderRadius.base};
  box-shadow: ${shadow.sm};
  border: 1px solid ${borderToken.subtle};
  white-space: normal;
  width: max-content;
  max-width: 240px;
  z-index: ${zIndexTokens.overlay};
  pointer-events: none;
`;

interface InfoTooltipTriggerProps {
  text: string;
}

const InfoTooltipTrigger = ({ text }: InfoTooltipTriggerProps) => {
  const iconRef = useRef<HTMLSpanElement>(null);
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });

  const updatePosition = useCallback(() => {
    const el = iconRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setPos({
      top: rect.top - 6,
      left: rect.left + rect.width / 2,
    });
  }, []);

  const handleEnter = useCallback(() => {
    updatePosition();
    setVisible(true);
  }, [updatePosition]);

  const handleLeave = useCallback(() => {
    setVisible(false);
  }, []);

  useLayoutEffect(() => {
    if (!visible) return;
    updatePosition();
  }, [visible, updatePosition]);

  return (
    <>
      <IconWrapper
        ref={iconRef}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
      >
        <InfoIcon />
      </IconWrapper>
      {visible &&
        createPortal(
          <TooltipBubble
            style={{
              top: pos.top,
              left: pos.left,
              transform: "translate(-50%, -100%)",
            }}
          >
            {text}
          </TooltipBubble>,
          document.body,
        )}
    </>
  );
};

export default InfoTooltipTrigger;
